// Pure helpers for the AHA-6681 ducted detail Ratings enrichment: the
// tolerant read of the server's per-row displayAttributes (which since the
// derive-aware filtering work carries the FULLY resolved effective values —
// kept-equipment AND estimate tiers are server-side, so the card renders
// exactly what the range filters evaluated), the tonnage label, and the
// row-applicability matrix that HIDES structurally irrelevant metrics
// instead of dashing them. SQL/UI-free so every rule is unit-testable.

export type TonnageRange = { min: number; max: number };

// One server-derived (non-native) performance value with its provenance:
// 'kept' carries the kept component's EE productCode (e.g. 'UF') for the
// "from your furnace" label; 'estimate' is a Generic-spec estimate.
export type DerivedMetric = {
  value: number;
  source: 'kept' | 'estimate';
  keptProductCode?: string;
};

// GUI twin of pnet's CombinationDisplayAttributes (rides each combination
// row through the ss-api passthrough untyped).
export type DisplayAttributes = {
  tonnage: TonnageRange | null;
  refrigerant: string[];
  stages: string[];
  fuelType: string[];
  derived: {
    coolingCapacity?: DerivedMetric;
    heatingCapacity?: DerivedMetric;
    afue?: DerivedMetric;
  };
  // Actively-range-filtered fields the server could not evaluate for this
  // combo (kept pairings null-pass those filters) — feeds the card's
  // "no data" explanation. Empty for the alternatives set.
  unevaluatedFilterFields: string[];
};

const emptyDisplayAttributes = (): DisplayAttributes => ({
  tonnage: null,
  refrigerant: [],
  stages: [],
  fuelType: [],
  derived: {},
  unevaluatedFilterFields: []
});

const readPositiveNumber = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const readStringArray = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((v): v is string => typeof v === 'string' && v !== '')
    : [];

// Tolerant read of one derived-metric entry; anything malformed (including
// rows from the pre-derive `estimated` payload shape) degrades to null.
function readDerivedMetric(raw: unknown): DerivedMetric | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const candidate = raw as Record<string, unknown>;
  const value = readPositiveNumber(candidate.value);
  const { source } = candidate;
  if (value === null || (source !== 'kept' && source !== 'estimate')) {
    return null;
  }
  const metric: DerivedMetric = { value, source };
  if (
    typeof candidate.keptProductCode === 'string' &&
    candidate.keptProductCode !== ''
  ) {
    metric.keptProductCode = candidate.keptProductCode;
  }
  return metric;
}

// Tolerant read of the untyped row's displayAttributes: anything malformed
// degrades to the empty shape (rows from older cached responses simply
// have no attributes).
export function parseDisplayAttributes(
  combination: Record<string, unknown>
): DisplayAttributes {
  const raw = combination.displayAttributes;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptyDisplayAttributes();
  }
  const candidate = raw as Record<string, unknown>;
  const out = emptyDisplayAttributes();

  const { tonnage } = candidate;
  if (
    tonnage !== null &&
    typeof tonnage === 'object' &&
    !Array.isArray(tonnage)
  ) {
    const min = readPositiveNumber((tonnage as Record<string, unknown>).min);
    const max = readPositiveNumber((tonnage as Record<string, unknown>).max);
    if (min !== null && max !== null) out.tonnage = { min, max };
  }
  out.refrigerant = readStringArray(candidate.refrigerant);
  out.stages = readStringArray(candidate.stages);
  out.fuelType = readStringArray(candidate.fuelType);

  const { derived } = candidate;
  if (
    derived !== null &&
    typeof derived === 'object' &&
    !Array.isArray(derived)
  ) {
    const source = derived as Record<string, unknown>;
    for (const key of ['coolingCapacity', 'heatingCapacity', 'afue'] as const) {
      const metric = readDerivedMetric(source[key]);
      if (metric !== null) out.derived[key] = metric;
    }
  }
  out.unevaluatedFilterFields = readStringArray(
    candidate.unevaluatedFilterFields
  );
  return out;
}

// Same copy as tonnage-logic's tonnageRangeLabel ("3 Ton", "2.5 – 3.5 Ton")
// but over the resolved spec range — NOT reused directly because that
// helper snaps to the tile scale via tonnageSpan and would drop off-scale
// spec values.
export function formatTonnageLabel(range: TonnageRange | null): string | null {
  if (!range) return null;
  return range.min === range.max
    ? `${range.min} Ton`
    : `${range.min} – ${range.max} Ton`;
}

// ---------------------------------------------------------------------------
// Position classification (shared by the tier-1 fallback and the matrix)
// ---------------------------------------------------------------------------

// Generic product-code families, mirroring pnet's tier-2 estimator: outdoor
// codes that can own combo heating performance, gas-burning packaged codes,
// and furnace blower codes.
const HEAT_PUMP_OUTDOOR_CODES = new Set(['HP', 'SC', 'PH', 'PD']);
const GAS_PACK_OUTDOOR_CODES = new Set(['PG', 'PD']);
const FURNACE_BLOWER_CODES = new Set(['UF', 'DF', 'MF']);

// `${ProductCode}${GenericModelID}` → the 2-letter product code ('UF38' →
// 'UF'); null when absent or unparseable (Generic positions may carry a
// null model number in older rows).
function genericPositionCode(
  combination: Record<string, unknown>,
  manufacturerKey: string,
  modelKey: string
): string | null {
  if (combination[manufacturerKey] !== 'Generic') return null;
  const model = combination[modelKey];
  if (typeof model !== 'string' || model.length < 3) return null;
  return /^\d+$/.test(model.slice(2)) ? model.slice(0, 2) : null;
}

const hasText = (value: unknown): boolean =>
  typeof value === 'string' && value.length > 0;

// ---------------------------------------------------------------------------
// Kept-derived value presentation
// ---------------------------------------------------------------------------

// Suffix phrase for a server kept-derived value ("~60,000 BTU · from your
// furnace" treatment), keyed by the kept component's EE productCode.
export function keptSourcePhrase(keptProductCode: string | undefined): string {
  if (keptProductCode && FURNACE_BLOWER_CODES.has(keptProductCode)) {
    return 'from your furnace';
  }
  if (keptProductCode && HEAT_PUMP_OUTDOOR_CODES.has(keptProductCode)) {
    return 'from your heat pump';
  }
  return 'from your equipment';
}

// Human labels for the server's unevaluatedFilterFields ids (the
// combination-source range-filter fields), for the card's "no data" note.
const FILTER_FIELD_LABELS: Record<string, string> = {
  SEER2: 'SEER2',
  EER2: 'EER2',
  HSPF2: 'HSPF2',
  COP5: 'COP @ 5°F',
  AFUE: 'AFUE',
  CoolingCapacity: 'Cooling Capacity',
  HeatingCapacity: 'Heating Capacity'
};

// The filtered ratings the server could not evaluate for this combo, as
// human labels (empty for alternatives rows — the server only flags kept
// pairings).
export function unevaluatedFilterLabels(
  combination: Record<string, unknown>
): string[] {
  return parseDisplayAttributes(combination).unevaluatedFilterFields.map(
    (field) => FILTER_FIELD_LABELS[field] ?? field
  );
}

// ---------------------------------------------------------------------------
// Applicability matrix (Part 3): hide what can NEVER apply, dash the rest
// ---------------------------------------------------------------------------

export type RatingRowKey =
  | 'SEER2'
  | 'EER2'
  | 'HSPF2'
  | 'COP5'
  | 'AFUE'
  | 'CoolingCapacity'
  | 'HeatingCapacity'
  | 'tonnage'
  | 'refrigerant'
  | 'stages'
  | 'fuelType';

// Composition-derived visibility. Signals corroborate each other and fail
// open: a combo whose shape can't be classified keeps its rows visible with
// a dash (honest "applicable but unknown"). SystemTypeLabel is DB data —
// only word tests, never exact strings.
export function ratingsApplicability(
  combination: Record<string, unknown>,
  attributes: DisplayAttributes
): Record<RatingRowKey, boolean> {
  const outdoorPresent =
    hasText(combination.OutdoorManufacturer) ||
    hasText(combination.OutdoorModelNumber);
  const outdoorCode = genericPositionCode(
    combination,
    'OutdoorManufacturer',
    'OutdoorModelNumber'
  );
  const blowerCode = genericPositionCode(
    combination,
    'BlowerManufacturer',
    'BlowerModelNumber'
  );
  const label =
    typeof combination.SystemTypeLabel === 'string'
      ? combination.SystemTypeLabel
      : '';

  const isHeatPump =
    (outdoorCode !== null && HEAT_PUMP_OUTDOOR_CODES.has(outdoorCode)) ||
    combination.HSPF2 != null ||
    combination.COP5 != null ||
    /heat pump/i.test(label);

  const hasFurnace =
    (blowerCode !== null && FURNACE_BLOWER_CODES.has(blowerCode)) ||
    (outdoorCode !== null && GAS_PACK_OUTDOOR_CODES.has(outdoorCode)) ||
    combination.AFUE != null ||
    attributes.fuelType.length > 0 ||
    /furnace|gas/i.test(label);

  return {
    SEER2: outdoorPresent,
    EER2: outdoorPresent,
    CoolingCapacity: outdoorPresent,
    tonnage: outdoorPresent,
    // A coil-carrying combo still has a circuit refrigerant even without an
    // outdoor unit; furnace-only combos hide the row.
    refrigerant: outdoorPresent || attributes.refrigerant.length > 0,
    HSPF2: isHeatPump,
    COP5: isHeatPump,
    // Heating capacity applies to BOTH heat pumps and furnaces.
    HeatingCapacity: isHeatPump || hasFurnace,
    AFUE: hasFurnace,
    fuelType: hasFurnace,
    // Per spec: hidden when no component carries stages data.
    stages: attributes.stages.length > 0
  };
}
